import React, { useState, useEffect, useRef } from "react";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  Rating,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AddCommentIcon from "@mui/icons-material/AddComment";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StarIcon from "@mui/icons-material/Star";
import { useAppSelector, useAppDispatch } from "../../../application/states/hooks";
import {
  setChatOpen,
  addMessage,
  addBotResponse,
  setConversationId,
  setChatLoading,
  clearChat,
  IChatMessage,
} from "../../../application/states/chat";
import { useChatApi } from "../../../infra/chat-api";
import { useNavigationService } from "../../../use-cases/navigation";
import { useSettingsService } from "../../../use-cases/settings";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { darkenColor } from "../../../utils";
import { HELP_SCROLL_STORAGE_KEY, HELP_SCROLL_EVENT } from "../../pages/workspace/settings/help-section/HelpSection";

/** Quebras de linha preservadas; **texto** vira negrito */
function formatMessageText(text: string) {
  const parts = text.split("**");
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

export const ChatWidget: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isOpen, messages, conversationId, isLoading } = useAppSelector(
    (state) => state.chat
  );
  const user = useAppSelector((state) => state.user);
  const company = useAppSelector((state) => state.company.company);
  const resaleConfig = useAppSelector((state) => state.resaleConfig.resaleConfig);
  const { sendMessage, submitEvaluation } = useChatApi();
  const { navigateTo } = useNavigationService();
  const { setActiveTab } = useSettingsService();
  const [inputMessage, setInputMessage] = useState("");
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [evaluationRating, setEvaluationRating] = useState<number | null>(null);
  const [evaluationComment, setEvaluationComment] = useState("");
  const [evaluationSubmitting, setEvaluationSubmitting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const isResale = company?.resale === true || !!company?.resaleId;

  const brandName = isResale
    ? resaleConfig?.name || company?.nomeFantasia || company?.razaoSocial || "Lurik"
    : "Lurik";

  const WELCOME_MESSAGE_TEXT = `**Olá! Eu sou o Luk 😊**

**Seu assistente aqui na ${brandName}.**

Estou aqui para te ajudar com o que você precisar — e pode ficar à vontade para perguntar qualquer coisa, viu? Toda pergunta é uma boa pergunta por aqui.

Comigo você pode:

- **Entender qualquer funcionalidade da plataforma** de um jeito simples e direto.
- **Receber orientações passo a passo** para realizar uma atividade.
${!isResale ? `- **Acessar trechos do ${brandName} 360° - Manual Completo do Usuário** quando quiser ver imagens e detalhes completos.\n` : ""}- **Consultar a FAQ** com as dúvidas mais comuns.
- **Ser direcionado para o suporte humano** sempre que for necessário (e eu vou te avisar quando isso fizer mais sentido).

Eu funciono por **palavras-chave**, então caso eu não consiga responder alguma dúvida, tente reformular sua pergunta. Caso ainda assim não funcione, você pode solicitar **"atendimento humano"** para falar com nossos especialistas.

Estou por aqui para deixar sua experiência mais fácil, leve e clara.

**Como posso te ajudar hoje?** 😊`;

  const handleOpenChat = () => {
    dispatch(setChatOpen(true));
    // Adiciona mensagem de boas-vindas se não houver mensagens
    if (messages.length === 0) {
      const welcomeMessage: IChatMessage = {
        id: `bot_${Date.now()}`,
        text: WELCOME_MESSAGE_TEXT,
        sender: "bot",
        timestamp: new Date(),
      };
      dispatch(addMessage(welcomeMessage));
    }
  };

  const handleCloseChat = () => {
    const hadConversation = messages.length > 1 && user?.id;
    if (hadConversation) {
      setEvaluationRating(null);
      setEvaluationComment("");
      setShowEvaluationModal(true);
    } else {
      dispatch(setChatOpen(false));
    }
  };

  const handleEvaluationSkip = () => {
    setShowEvaluationModal(false);
    dispatch(setChatOpen(false));

    // Reset chat for next time
    dispatch(clearChat());
    const welcomeMessage: IChatMessage = {
      id: `bot_${Date.now()}`,
      text: WELCOME_MESSAGE_TEXT,
      sender: "bot",
      timestamp: new Date(),
    };
    dispatch(addMessage(welcomeMessage));
  };

  const handleEvaluationSubmit = async () => {
    if (evaluationRating == null || !user?.id) return;
    setEvaluationSubmitting(true);
    try {
      await submitEvaluation({
        userId: user.id,
        rating: evaluationRating,
        conversationId: conversationId || undefined,
        comment: evaluationComment || undefined,
      });
    } catch (_) {
      // ignore errors; still close
    } finally {
      setEvaluationSubmitting(false);
      setShowEvaluationModal(false);
      dispatch(setChatOpen(false));

      // Reset chat for next time
      dispatch(clearChat());
      const welcomeMessage: IChatMessage = {
        id: `bot_${Date.now()}`,
        text: WELCOME_MESSAGE_TEXT,
        sender: "bot",
        timestamp: new Date(),
      };
      dispatch(addMessage(welcomeMessage));
    }
  };

  const handleNewChat = () => {
    dispatch(clearChat());
    const welcomeMessage: IChatMessage = {
      id: `bot_${Date.now()}`,
      text: WELCOME_MESSAGE_TEXT,
      sender: "bot",
      timestamp: new Date(),
    };
    dispatch(addMessage(welcomeMessage));
  };

  const handleSendMessage = async (messageToSend?: string) => {
    const text = (messageToSend !== undefined && messageToSend !== "") ? messageToSend.trim() : inputMessage.trim();
    if (!text || isLoading) return;

    const userMessage: IChatMessage = {
      id: `user_${Date.now()}`,
      text,
      sender: "user",
      timestamp: new Date(),
    };

    dispatch(addMessage(userMessage));
    setInputMessage("");
    dispatch(setChatLoading(true));

    try {
      const response = await sendMessage({
        userId: user.id!,
        message: userMessage.text,
        conversationId: conversationId,
        context: {
          currentPage: window.location.pathname,
          companyId: company?.id || "",
          userId: user.id || "",
          userType: user.userType,
          companyName: resaleConfig?.name || (company?.resale === true || company?.resaleId ? company?.nomeFantasia || company?.razaoSocial : "Lurik"),
          isResale: company?.resale === true || !!company?.resaleId,
        },
      });

      if (response.conversationId) {
        dispatch(setConversationId(response.conversationId));
      }

      dispatch(
        addBotResponse({
          text: response.response,
          suggestions: response.suggestions,
          actions: response.actions,
        })
      );

      if (response.requestEvaluation && user?.id) {
        setEvaluationRating(null);
        setEvaluationComment("");
        setTimeout(() => setShowEvaluationModal(true), 800);
      }
    } catch (error: any) {
      dispatch(
        addBotResponse({
          text:
            error.message ||
            "Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.",
        })
      );
    } finally {
      dispatch(setChatLoading(false));
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
    // Envia o texto da sugestão diretamente; não depender do state evita closure (envio da mensagem errada)
    handleSendMessage(suggestion);
  };

  const handleNavigateAction = (tab: "help" | "faqs", pdfPage?: number) => {
    if (pdfPage != null) {
      try {
        sessionStorage.setItem(HELP_SCROLL_STORAGE_KEY, String(pdfPage));
      } catch { }
    }
    setActiveTab({ tab });
    navigateTo({ route: "/workspace/settings" });
    handleCloseChat();
    // Dispara evento para a HelpSection (quando já está na aba Ajuda) fazer o scroll para a página
    if (tab === "help" && pdfPage != null) {
      window.dispatchEvent(new CustomEvent(HELP_SCROLL_EVENT));
    }
  };

  const handleSendClick = () => {
    handleSendMessage();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Botão Flutuante */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleOpenChat}
        sx={{
          position: "fixed",
          bottom: "5%",
          right: "5%",
          zIndex: 1300,
          "&:hover": {
            backgroundColor:
              company?.resale === true || company?.resaleId
                ? darkenColor(theme.palette.primary.main, 10) // Allows theme color if set correctly
                : darkenColor(theme.palette.primary.main, 10),
          },
        }}
      >
        <ChatIcon
          sx={{
            color:
              company?.resale === true || company?.resaleId
                ? theme.palette.primary.contrastText
                : "#FFF",
          }}
        />
      </Fab>

      {/* Dialog do Chat */}
      <Dialog
        open={isOpen}
        onClose={handleCloseChat}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            bottom: 80,
            right: 20,
            margin: 0,
            maxHeight: "70vh",
            height: "600px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color:
              company?.resale === true || company?.resaleId
                ? theme.palette.primary.contrastText
                : "#FFF",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <SmartToyIcon />
            <Typography variant="h6">Assistente Virtual</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Novo chat">
              <IconButton
                onClick={handleNewChat}
                aria-label="Novo chat"
                sx={{
                  color:
                    company?.resale === true || company?.resaleId
                      ? theme.palette.primary.contrastText
                      : "#FFF",
                }}
              >
                <AddCommentIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={handleCloseChat}
              aria-label="Fechar"
              sx={{
                color:
                  company?.resale === true || company?.resaleId
                    ? theme.palette.primary.contrastText
                    : "#FFF",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Área de Mensagens */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: 2,
              backgroundColor: "#f5f5f5",
            }}
          >
            <List>
              {messages.map((message: IChatMessage) => (
                <ListItem
                  key={message.id}
                  sx={{
                    justifyContent:
                      message.sender === "user" ? "flex-end" : "flex-start",
                    paddingY: 1,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "70%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems:
                        message.sender === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        padding: 1.5,
                        backgroundColor:
                          message.sender === "user"
                            ? theme.palette.primary.main
                            : "#FFF",
                        color:
                          message.sender === "user"
                            ? company?.resale === true || company?.resaleId
                              ? theme.palette.primary.contrastText
                              : "#FFF"
                            : "#000",
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                        {formatMessageText(message.text)}
                      </Typography>
                    </Paper>
                    <Typography
                      variant="caption"
                      sx={{ marginTop: 0.5, color: "#666" }}
                    >
                      {format(message.timestamp, "HH:mm", { locale: ptBR })}
                    </Typography>

                    {/* Sugestões */}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          marginTop: 1,
                        }}
                      >
                        {message.suggestions.map((suggestion: string, index: number) => (
                          <Chip
                            key={index}
                            label={suggestion}
                            onClick={() => handleSuggestionClick(suggestion)}
                            size="small"
                            variant="outlined"
                            sx={{ cursor: "pointer" }}
                          />
                        ))}
                      </Box>
                    )}
                    {/* Ações (contato, navegar para Manual/FAQs) */}
                    {message.actions && message.actions.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          marginTop: 1,
                        }}
                      >
                        {message.actions.map((action: { type: string; label: string; value: any }, index: number) => {
                          if (action.type === "navigate" && action.value?.tab) {
                            return (
                              <Chip
                                key={index}
                                label={action.label}
                                onClick={() =>
                                  handleNavigateAction(
                                    action.value.tab,
                                    action.value.pdfPage
                                  )
                                }
                                size="small"
                                color="primary"
                                sx={{ cursor: "pointer" }}
                              />
                            );
                          }
                          if (action.type === "phone" && action.value) {
                            return (
                              <Chip
                                key={index}
                                label={action.label}
                                component="a"
                                href={`tel:${action.value}`}
                                clickable
                                size="small"
                                variant="outlined"
                                sx={{ cursor: "pointer" }}
                              />
                            );
                          }
                          if (action.type === "email" && action.value) {
                            return (
                              <Chip
                                key={index}
                                label={action.label}
                                component="a"
                                href={`mailto:${action.value}`}
                                clickable
                                size="small"
                                variant="outlined"
                                sx={{ cursor: "pointer" }}
                              />
                            );
                          }
                          return null;
                        })}
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem sx={{ justifyContent: "flex-start" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: 1,
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">
                      Digitando...
                    </Typography>
                  </Box>
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Área de Input */}
          <Box
            sx={{
              padding: 2,
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: "#FFF",
            }}
          >
            <Box display="flex" gap={1}>
              <TextField
                inputRef={inputRef}
                fullWidth
                placeholder="Digite sua mensagem..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                variant="outlined"
                size="small"
              />
              <IconButton
                color="primary"
                onClick={handleSendClick}
                disabled={!inputMessage.trim() || isLoading}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color:
                    company?.resale === true || company?.resaleId
                      ? theme.palette.primary.contrastText
                      : "#FFF",
                  "&:hover": {
                    backgroundColor: darkenColor(theme.palette.primary.main, 10),
                  },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de avaliação (opcional) ao fechar o chat */}
      <Dialog
        open={showEvaluationModal}
        onClose={handleEvaluationSkip}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 2 }}>
          Como foi sua experiência com o atendimento?
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", pb: 2 }}>

          <Rating
            name="chat-evaluation"
            value={evaluationRating}
            onChange={(_, value) => setEvaluationRating(value ?? null)}
            size="large"
            icon={<StarIcon fontSize="inherit" />}
            emptyIcon={<StarIcon fontSize="inherit" sx={{ opacity: 0.3 }} />}
            max={5}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="O que achou bom ou ruim? (opcional)"
            value={evaluationComment}
            onChange={(e) => setEvaluationComment(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 500 }}
          />
          <Box sx={{ display: "flex", gap: 1, width: "100%", justifyContent: "center" }}>
            <Button variant="outlined" onClick={handleEvaluationSkip}>
              Pular
            </Button>
            <Button
              variant="contained"
              onClick={handleEvaluationSubmit}
              disabled={evaluationRating == null || evaluationSubmitting}
              startIcon={evaluationSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                '&:hover': {
                  backgroundColor: company?.resale === true || company?.resaleId
                    ? resaleConfig?.theme.colors.secondary.main
                    : undefined,
                },
              }}
            >
              {evaluationSubmitting ? "Enviando…" : "Enviar avaliação"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
